import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, Check, Loader2, ShieldCheck } from "lucide-react";
import { submitSmsOptin } from "@/lib/api";
import { toast } from "sonner";

const BRAND = "Mindful Minerals";

function formatPhone(v) {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export default function TextAlerts() {
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const digits = phone.replace(/\D/g, "");
  const canSubmit = digits.length === 10 && consent && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please check the consent box to continue.");
    if (digits.length !== 10) return toast.error("Enter a valid 10-digit mobile number.");
    setSubmitting(true);
    try {
      await submitSmsOptin({ phone: digits, consent, firstName: firstName.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete sign-up. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4" data-testid="text-alerts-page">
      <div className="mx-auto max-w-md">
        <div className="rounded-2xl bg-white shadow-sm border border-zinc-200 overflow-hidden">
          <div className="bg-zinc-900 px-6 py-7 text-white">
            <div className="flex items-center gap-2.5">
              <MessageSquareText size={22} className="text-emerald-400" />
              <h1 className="text-lg font-semibold">{BRAND} Text Alerts</h1>
            </div>
            <p className="mt-2 text-sm text-zinc-300">
              Sign up to get exclusive offers and updates by text.
            </p>
          </div>

          {done ? (
            <div className="px-6 py-10 text-center" data-testid="optin-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                <Check size={26} className="text-emerald-600" />
              </div>
              <h2 className="mt-4 text-base font-semibold text-zinc-900">You're signed up!</h2>
              <p className="mt-2 text-sm text-zinc-500">
                We just sent a confirmation text to your phone. Reply <b>STOP</b> anytime to
                unsubscribe, or <b>HELP</b> for help.
              </p>
              <Link
                to="/"
                className="mt-6 inline-block text-sm font-medium text-emerald-600 hover:underline"
                data-testid="optin-home-link"
              >
                Back to home
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
              <div>
                <label className="text-sm font-medium text-zinc-700">First name (optional)</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                  data-testid="optin-firstname"
                  className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700">
                  Mobile Phone Number<span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  required
                  data-testid="optin-phone"
                  className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="optin-consent"
                  className="mt-0.5 h-5 w-5 shrink-0 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs leading-relaxed text-zinc-600">
                  Yes, I would like to receive recurring automated marketing and account text
                  messages (promotions, new product announcements, and order/account updates)
                  from {BRAND} / OreVital at the number provided. Consent is not a condition of
                  purchase.
                </span>
              </label>

              <div className="rounded-lg bg-zinc-50 px-3.5 py-3 text-[11px] leading-relaxed text-zinc-500 space-y-1.5">
                <p>
                  <b className="text-zinc-700">Message Frequency:</b> You will receive up to 4
                  messages per month.
                </p>
                <p>
                  <b className="text-zinc-700">Standard Rates:</b> Message and data rates may apply
                  depending on your mobile phone service plan.
                </p>
                <p>
                  <b className="text-zinc-700">Help &amp; Stop:</b> Reply HELP for help or STOP to
                  cancel at any time.
                </p>
                <p>
                  See our{" "}
                  <Link to="/text-alerts/terms" className="text-emerald-600 hover:underline" data-testid="optin-terms-link">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/text-alerts/privacy" className="text-emerald-600 hover:underline" data-testid="optin-privacy-link">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="optin-submit"
                className="w-full rounded-lg bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={16} /> Signing up…
                  </span>
                ) : (
                  "Yes, sign me up!"
                )}
              </button>

              <p className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-400">
                <ShieldCheck size={13} /> We never sell your number. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
